export type HistoryPoint = { t: number; p: number };
const DAY = 86400;

export function cleanMarketHistory(
  value: unknown,
  now = Date.now(),
): HistoryPoint[] {
  if (!Array.isArray(value)) return [];
  const points = new Map<number, number | null>();
  for (const point of value) {
    if (
      typeof point?.t !== "number" ||
      typeof point?.p !== "number" ||
      !Number.isFinite(point.t) ||
      !Number.isFinite(point.p) ||
      point.p <= 0 ||
      point.t <= 0 ||
      point.t * 1000 > now
    )
      continue;
    const previous = points.get(point.t);
    points.set(
      point.t,
      previous === undefined || previous === point.p ? point.p : null,
    );
  }
  return [...points]
    .flatMap(([t, p]) => (p === null ? [] : [{ t, p }]))
    .sort((a, b) => a.t - b.t);
}

export function variantMarketHistory(raw: unknown): HistoryPoint[] {
  try {
    const value = typeof raw === "string" ? JSON.parse(raw) : raw;
    return cleanMarketHistory([
      ...(Array.isArray(value?.priceHistory) ? value.priceHistory : []),
      ...(Array.isArray(value?.priceHistory30d) ? value.priceHistory30d : []),
    ]);
  } catch {
    return [];
  }
}

// Compare actual observations around the requested boundary. Never extend a
// short series, interpolate prices, or turn a missing period into zero change.
export function marketHistoryWindow(
  history: HistoryPoint[],
  days: number,
  now = Date.now(),
) {
  const points = cleanMarketHistory(history, now);
  const latest = points.at(-1);
  if (!latest || now / 1000 - latest.t > 14 * DAY) return null;
  const cutoff = latest.t - days * DAY;
  const baseline = points.filter((point) => point.t <= cutoff).at(-1);
  if (!baseline || cutoff - baseline.t > Math.min(2, days) * DAY) return null;
  const sample = points.filter((point) => point.t >= cutoff);
  return {
    days,
    changePercent: (latest.p / baseline.p - 1) * 100,
    start: baseline.t,
    end: latest.t,
    low: Math.min(...sample.map((point) => point.p)),
    high: Math.max(...sample.map((point) => point.p)),
  };
}

export type OwnedHistoryItem = {
  quantity?: number | null;
  language: string;
  grading_company_id?: string | null;
  grade_id?: string | null;
  language_review_needed?: boolean;
  pricing?: { identity: { cardId: number; cardFormat: string; language: string } };
  variant: { card_id: number; raw: unknown; card: { language?: string | null } };
};

/** Provider-guide history for a fixed cohort of today's holdings, not past inventory. */
export function ownedMarketHistory(items: OwnedHistoryItem[], now = Date.now()) {
  let totalCopies = 0;
  let coveredCopies = 0;
  const series: { points: HistoryPoint[]; quantity: number }[] = [];
  for (const item of items) {
    const quantity = Number(item.quantity ?? 1);
    if (!Number.isFinite(quantity) || quantity <= 0) continue;
    totalCopies += quantity;
    const identity = item.pricing?.identity;
    if (
      item.grading_company_id || item.grade_id || item.language_review_needed ||
      !identity || identity.cardFormat !== "raw" ||
      identity.cardId !== item.variant.card_id ||
      identity.language !== item.language ||
      (item.variant.card.language ?? "english") !== item.language
    ) continue;
    const points = variantMarketHistory(item.variant.raw);
    if (points.length < 2 || now / 1000 - points.at(-1)!.t > 14 * DAY) continue;
    coveredCopies += quantity;
    series.push({ points, quantity });
  }
  // Require an observation within two days for every member of the same cohort.
  // Missing cards never turn into zero, and coverage cannot change mid-chart.
  const dates = [...new Set(series.flatMap(({ points }) =>
    points.map(({ t }) => Math.floor(t / DAY) * DAY),
  ))].sort((a, b) => a - b);
  const points = dates.flatMap((t) => {
    let p = 0;
    for (const entry of series) {
      const observation = entry.points.filter((point) => point.t < t + DAY).at(-1);
      if (!observation || t + DAY - observation.t > 2 * DAY) return [];
      p += observation.p * entry.quantity;
    }
    return series.length ? [{ t, p: Math.round(p * 100) / 100 }] : [];
  });
  return { points, coveredCopies, totalCopies };
}

export function historyForPeriod(points: HistoryPoint[], days: number, now = Date.now()) {
  const cutoff = now / 1000 - days * DAY;
  return points.filter((point) => point.t >= cutoff && point.t * 1000 <= now);
}
